import type { Geometry, Position } from './types';

const EARTH_RADIUS_METERS = 6_371_008.8;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function normalizeLongitude(longitude: number): number {
  return ((longitude + 180) % 360 + 360) % 360 - 180;
}

export function haversineDistance(a: Position, b: Position): number {
  const lat1 = a[1] * DEG_TO_RAD;
  const lat2 = b[1] * DEG_TO_RAD;
  const deltaLat = (b[1] - a[1]) * DEG_TO_RAD;
  const deltaLon = (b[0] - a[0]) * DEG_TO_RAD;
  const sinLat = Math.sin(deltaLat / 2);
  const sinLon = Math.sin(deltaLon / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function lineDistance(coordinates: Position[]): number {
  let total = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    total += haversineDistance(coordinates[index - 1], coordinates[index]);
  }
  return total;
}

export function sphericalPolygonArea(ring: Position[]): number {
  if (ring.length < 3) return 0;
  let sum = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const current = ring[index];
    const next = ring[(index + 1) % ring.length];
    sum += (next[0] - current[0]) * DEG_TO_RAD
      * (2 + Math.sin(current[1] * DEG_TO_RAD) + Math.sin(next[1] * DEG_TO_RAD));
  }
  return Math.abs(sum * EARTH_RADIUS_METERS * EARTH_RADIUS_METERS / 2);
}

export function geometryCenter(geometry: Geometry): Position {
  const positions: Position[] = [];
  visitPositions(geometry, (position) => positions.push(position));
  if (!positions.length) return [0, 0, 0];
  const cartesian = positions.reduce(
    (accumulator, [longitude, latitude]) => {
      const lon = longitude * DEG_TO_RAD;
      const lat = latitude * DEG_TO_RAD;
      accumulator.x += Math.cos(lat) * Math.cos(lon);
      accumulator.y += Math.cos(lat) * Math.sin(lon);
      accumulator.z += Math.sin(lat);
      return accumulator;
    },
    { x: 0, y: 0, z: 0 },
  );
  const longitude = Math.atan2(cartesian.y, cartesian.x) * RAD_TO_DEG;
  const hypotenuse = Math.sqrt(cartesian.x ** 2 + cartesian.y ** 2);
  const latitude = Math.atan2(cartesian.z, hypotenuse) * RAD_TO_DEG;
  return [normalizeLongitude(longitude), clamp(latitude, -89.9, 89.9), 0];
}

export function geometryBounds(geometry: Geometry): [number, number, number, number] {
  let west = 180;
  let south = 90;
  let east = -180;
  let north = -90;
  visitPositions(geometry, ([longitude, latitude]) => {
    west = Math.min(west, longitude);
    south = Math.min(south, latitude);
    east = Math.max(east, longitude);
    north = Math.max(north, latitude);
  });
  return [west, south, east, north];
}

export function visitPositions(geometry: Geometry, visitor: (position: Position) => void): void {
  switch (geometry.type) {
    case 'Point':
      visitor(geometry.coordinates);
      break;
    case 'MultiPoint':
    case 'LineString':
      geometry.coordinates.forEach(visitor);
      break;
    case 'MultiLineString':
    case 'Polygon':
      geometry.coordinates.forEach((line) => line.forEach(visitor));
      break;
    case 'MultiPolygon':
      geometry.coordinates.forEach((polygon) => polygon.forEach((line) => line.forEach(visitor)));
      break;
    default:
      break;
  }
}

export function formatDistance(meters: number): string {
  if (meters >= 1_000_000) return `${(meters / 1_000_000).toFixed(2)} Mm`;
  if (meters >= 1_000) return `${(meters / 1_000).toFixed(2)} km`;
  return `${meters.toFixed(0)} m`;
}

export function formatArea(squareMeters: number): string {
  if (squareMeters >= 1_000_000) return `${(squareMeters / 1_000_000).toFixed(2)} km²`;
  return `${squareMeters.toFixed(0)} m²`;
}
